import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { getSocialProof, sendPresence, getPresence, fetchCars, getSocialArchived } from "../api";

const SID_KEY = "cm_sid";
const DISMISS_KEY = "cm_sp_dismissed";

function getSid() {
  let s = sessionStorage.getItem(SID_KEY);
  if (!s) {
    s = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(SID_KEY, s);
  }
  return s;
}

function titlecase(x) {
  return (x || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function shortLabel(e) {
  if (e.brand) {
    const model = (e.model || "").split(/\s+/).slice(0, 1).join(" ");
    return `${titlecase(e.brand)} ${model}`.trim();
  }
  return (e.label || "Un'auto").split(/\s+/).slice(0, 2).join(" ");
}

function eventMessage(e) {
  const label = shortLabel(e);
  return e.type === "sold"
    ? `${label} ha trovato un nuovo proprietario`
    : `${label} è stata prenotata`;
}

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(s)) return "";
  if (s < 60) return "poco fa";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return h === 1 ? "1 ora fa" : `${h} ore fa`;
  const d = Math.floor(h / 24);
  if (d === 1) return "ieri";
  if (d < 7) return `${d} giorni fa`;
  const w = Math.floor(d / 7);
  return w === 1 ? "1 settimana fa" : `${w} settimane fa`;
}

// Marketing activity pool (owner-authorised): real catalog cars + first names only
// (no surnames / no public personal data) + Turin-area towns, shown on loop.
const NAMES = [
  "Marco", "Giulia", "Luca", "Sara", "Andrea", "Chiara", "Davide", "Elena",
  "Matteo", "Francesca", "Simone", "Alessia", "Roberto", "Martina", "Paolo",
  "Valentina", "Alberto", "Ilaria", "Fabio", "Federica",
];
const CITIES = [
  // Torino / Piemonte
  "Torino", "Collegno", "Rivoli", "Grugliasco", "Moncalieri", "Chieri",
  "Nichelino", "Settimo Torinese", "Venaria Reale", "Alpignano", "Rivalta",
  "Pianezza", "Novara", "Alessandria", "Asti", "Cuneo", "Biella", "Vercelli",
  // resto d'Italia
  "Milano", "Roma", "Napoli", "Bologna", "Firenze", "Genova", "Verona",
  "Padova", "Brescia", "Bergamo", "Venezia", "Bari", "Palermo", "Catania",
  "Cagliari", "Trento", "Bolzano", "Parma", "Modena", "Reggio Emilia",
  "Perugia", "Pescara", "Ancona", "Trieste", "Como", "Varese", "Lecce",
  "Salerno", "Rimini", "Livorno", "La Spezia", "Pisa",
];
const TIME_LABELS = [
  "poco fa", "12 min fa", "34 min fa", "1 ora fa", "2 ore fa", "3 ore fa",
  "5 ore fa", "ieri", "2 giorni fa",
];
const DISTINCTIVE_LABELS = [
  "La più richiesta del momento",
  "La più visualizzata",
  "Tra le preferite dei clienti",
];
const REVIEW_ITEM = {
  key: "review",
  thumbnail: null,
  message: "★ 4,8 su Google · +50 recensioni reali",
  timeLabel: "su Google",
  review: true,
};

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function carModel(c) {
  const brand = c.brand ? titlecase(c.brand) : "";
  const trimFirst = (c.trim || "").split(/\s+/)[0] || "";
  const m = `${brand} ${trimFirst}`.trim();
  return m || (c.title || "auto").split(/\s+/).slice(0, 2).join(" ");
}

// Social-proof activity (owner-authorised marketing), first names only — no
// surnames / no public personal data. RULES:
//  - Cars STILL for sale (live catalog): only "inviato una richiesta" /
//    "sta osservando", plus 3 distinctive highlights + real Google reviews.
//  - "prenotato / comprato / bloccato" ONLY on ARCHIVED products (offline).
function generateActivity(liveCars, archivedCars) {
  const live = shuffle((liveCars || []).filter((c) => c.image));
  const archived = shuffle((archivedCars || []).filter((c) => c.image));
  const names = shuffle(NAMES);
  const cities = shuffle(CITIES);
  let ni = 0;
  let ci = 0;
  const nextName = () => names[ni++ % names.length];
  const nextCity = () => cities[ci++ % cities.length];

  // Distinctive: only 3 live products.
  const distinctive = live.slice(0, 3).map((c, i) => ({
    key: `dist:${c.handle}`,
    thumbnail: c.image,
    message: `${carModel(c)} · ${DISTINCTIVE_LABELS[i]}`,
    timeLabel: "in evidenza",
  }));

  // Interest on live cars (still available).
  const interest = live.slice(3, 13).map((c, i) => {
    const name = nextName();
    const city = nextCity();
    const m = carModel(c);
    const tpl = [
      `${name} da ${city} ha inviato una richiesta`,
      `${name} sta osservando questa ${m}`,
    ];
    return {
      key: `int:${c.handle}:${i}`,
      thumbnail: c.image,
      message: tpl[i % tpl.length],
      timeLabel: TIME_LABELS[i % TIME_LABELS.length],
    };
  });

  // Sold/reserved ONLY from archived (offline) products.
  const sold = archived.slice(0, 10).map((c, i) => {
    const name = nextName();
    const city = nextCity();
    const m = carModel(c);
    const tpl = [
      `${name} da ${city} ha prenotato una ${m}`,
      `${name} ha comprato una ${m}`,
      `${name} da ${city} ha bloccato una ${m}`,
    ];
    return {
      key: `sold:${i}`,
      thumbnail: c.image,
      message: tpl[i % tpl.length],
      timeLabel: TIME_LABELS[i % TIME_LABELS.length],
    };
  });

  // Round-robin interleave, sprinkling the real-review card.
  const base = [];
  const maxLen = Math.max(interest.length, sold.length, distinctive.length);
  for (let i = 0; i < maxLen; i += 1) {
    if (interest[i]) base.push(interest[i]);
    if (sold[i]) base.push(sold[i]);
    if (distinctive[i]) base.push(distinctive[i]);
  }
  const queue = [];
  base.forEach((it, idx) => {
    queue.push(it);
    if (idx % 4 === 3) queue.push({ ...REVIEW_ITEM });
  });
  if (!queue.length) return [];
  return queue;
}

export default function SocialProof() {
  const location = useLocation();
  const [notif, setNotif] = useState(null);
  const [shown, setShown] = useState(false);

  const reducedRef = useRef(false);
  const pathRef = useRef(location.pathname);
  const st = useRef({
    mount: Date.now(),
    lastShownAt: 0,
    count: 0,
    shownKeys: new Set(),
    visibleUntil: 0,
    dismissed: sessionStorage.getItem(DISMISS_KEY) === "1",
  });
  const data = useRef({ events: [], carsByHandle: {}, viewers: 0, city: null, carHandle: null, activity: [], actIdx: 0 });

  useEffect(() => { pathRef.current = location.pathname; }, [location.pathname]);

  // one-time: reduced-motion + catalog map + confirmed events
  useEffect(() => {
    if (window.matchMedia) {
      reducedRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    getSid();
    Promise.all([
      fetchCars().catch(() => ({ cars: [] })),
      getSocialArchived().catch(() => ({ cars: [] })),
    ]).then(([live, arch]) => {
      const map = {};
      (live.cars || []).forEach((c) => { map[c.handle] = c; });
      data.current.carsByHandle = map;
      data.current.activity = generateActivity(live.cars || [], arch.cars || []);
    });
    const loadEvents = () =>
      getSocialProof().then((r) => { data.current.events = r.events || []; }).catch(() => {});
    loadEvents();
    const evId = setInterval(loadEvents, 25000);
    return () => clearInterval(evId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time setup + interval
  }, []);

  // presence heartbeat + poll for the currently viewed car
  useEffect(() => {
    const m = /^\/auto\/([^/]+)/.exec(location.pathname);
    const handle = m ? m[1] : null;
    data.current.carHandle = handle;
    data.current.viewers = 0;
    data.current.city = null;
    if (!handle) return undefined;
    const sid = getSid();
    const beat = () => sendPresence(sid, handle).catch(() => {});
    const poll = () =>
      getPresence(handle, sid)
        .then((r) => { data.current.viewers = r.viewers || 0; data.current.city = r.city || null; })
        .catch(() => {});
    beat();
    poll();
    const b = setInterval(beat, 20000);
    const p = setInterval(poll, 12000);
    return () => { clearInterval(b); clearInterval(p); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-bind only on route change
  }, [location.pathname]);

  // main state machine
  useEffect(() => {
    const isBlocked = () => {
      if (pathRef.current.startsWith("/payment")) return true;
      if (document.querySelector('[role="dialog"]')) return true; // form/popup/fullscreen gallery
      return false;
    };
    const pick = () => {
      const s = st.current, d = data.current;
      for (const e of d.events) {
        const key = `e:${e.type}:${e.ts}:${e.thumbnail || ""}`;
        if (!s.shownKeys.has(key)) {
          return { key, thumbnail: e.thumbnail, message: eventMessage(e), ts: e.ts, live: false, real: true };
        }
      }
      if (d.carHandle && d.viewers > 0) {
        const key = `live:${d.carHandle}`;
        if (!s.shownKeys.has(key)) {
          const car = d.carsByHandle[d.carHandle];
          const message = d.city
            ? `Un visitatore da ${d.city} sta guardando questa auto`
            : "Un visitatore sta guardando questa auto";
          return { key, thumbnail: car ? car.image : null, message, live: true, real: true };
        }
      }
      // Fallback marketing activity, cycled on loop (advance index only on show).
      if (d.activity.length) {
        const item = d.activity[d.actIdx % d.activity.length];
        return { ...item, key: `${item.key}:${d.actIdx}`, marketing: true };
      }
      return null;
    };
    const hide = () => { st.current.visibleUntil = 0; setShown(false); setTimeout(() => setNotif(null), 300); };
    const tick = () => {
      const s = st.current, d = data.current;
      const now = Date.now();
      if (s.dismissed) return;
      if (s.visibleUntil) {
        if (isBlocked() || now >= s.visibleUntil) hide();
        return;
      }
      if (isBlocked()) return;
      if (now - s.mount < 10000) return; // first appearance after 10s
      const cand = pick();
      if (!cand) return;
      // Real events (paid reservation/sale, live visitor) appear promptly;
      // marketing activity keeps a calmer 30s spacing.
      const gap = cand.real ? 8000 : 30000;
      if (s.lastShownAt !== 0 && now - s.lastShownAt < gap) return;
      if (cand.marketing) d.actIdx += 1;
      s.lastShownAt = now;
      s.visibleUntil = now + 5000;
      s.shownKeys.add(cand.key);
      setNotif(cand);
      setTimeout(() => setShown(true), 30);
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- single scheduler, reads live refs
  }, []);

  const dismiss = () => {
    st.current.dismissed = true;
    sessionStorage.setItem(DISMISS_KEY, "1");
    setShown(false);
    setTimeout(() => setNotif(null), 250);
  };

  if (!notif) return null;
  return (
    <div
      className={`cm-sp${shown ? " cm-sp-in" : ""}`}
      data-testid="social-proof-card"
      role="status"
      aria-live="polite"
    >
      {notif.thumbnail && (
        <img src={notif.thumbnail} alt="" className="cm-sp-thumb" loading="lazy" />
      )}
      <div className="cm-sp-body">
        <p className="cm-sp-msg" data-testid="social-proof-message">{notif.message}</p>
        <p className="cm-sp-time">{notif.live ? "in questo momento" : (notif.timeLabel || timeAgo(notif.ts))}</p>
      </div>
      <button
        type="button"
        data-testid="social-proof-close"
        aria-label="Chiudi notifica"
        onClick={dismiss}
        className="cm-sp-x"
      >
        <X size={15} />
      </button>
    </div>
  );
}
